import { DateField } from "./DateField";
import { useState } from "react";
import {
  Banner,
  Button,
  Checkbox,
  DataTable,
  Dialog,
  Field,
  Input,
  PersonCell,
  Pill,
  SelectX,
  StatusPill,
  Textarea,
  useToast,
  type Column,
} from "@guestpilot/ui-kit";
import { api } from "../api/client";
import { useGet, useWrite } from "../api/hooks";
import type { PersonRef, Schema } from "../api/types";
import { fieldErrorsOf, useHandleApiError } from "../errors";
import { formatDate } from "../format";
import { useI18n } from "../i18n";
import { useSession } from "../session";
import { PersonPicker } from "./PersonPicker";

export type Participant = Schema<"Participant">;
export type Reason = Schema<"AccessReason">;
export interface Page<T> {
  items: T[];
  nextCursor?: string | null;
  total?: number;
}

/** A risk flag shown in place of a missing value ("No sponsor"), never colour alone (04 §6). */
export function Flag({ risk }: { risk: string }) {
  const { t } = useI18n();
  return <StatusPill domain="risk" value={risk} label={t(`ui:risk.${risk}`)} />;
}

const has = (p: Participant, r: string) => (p.risks ?? []).some((x) => x.type === r && !x.excepted);

export function ParticipantsTable({
  rows,
  loading,
  error,
  onRetry,
  showTeam,
  selectable,
  selected,
  onSelect,
  onEdit,
  onRemove,
  bulkBar,
  sticky,
  editLabel,
}: {
  rows: Participant[] | undefined;
  loading?: boolean;
  error?: boolean;
  onRetry?: () => void;
  showTeam?: boolean;
  selectable?: boolean;
  selected?: ReadonlySet<string>;
  onSelect?: (s: Set<string>) => void;
  onEdit?: (p: Participant) => void;
  onRemove?: (p: Participant) => void;
  bulkBar?: React.ReactNode;
  sticky?: boolean;
  editLabel?: string | undefined;
}) {
  const { t, locale } = useI18n();
  const s = useSession();
  const tz = s.me.tenant.timeZone;
  const toast = useToast();
  const fail = useHandleApiError();
  // a pending or scheduled removal can be called off by its requester or an Admin (BR-093, BR-213)
  const [cancelFor, setCancelFor] = useState<Participant | null>(null);
  const cancel = useWrite<unknown, { version: number }>("POST", "/api/v1/removals/:id/cancel", [
    "/api/v1/participants",
    "/api/v1/removals",
    "/api/v1/teams",
    "/api/v1/dashboard",
  ]);
  const cancelRemoval = async (p: Participant) => {
    try {
      const removal = await api.get<{ version: number }>(`/api/v1/removals/${p.openRemovalId}`);
      cancel.mutate(
        { params: { id: p.openRemovalId ?? "" }, body: { version: removal.version } },
        {
          onSuccess: () => {
            setCancelFor(null);
            toast.success(t("ui:removal.cancelDone"));
          },
          onError: fail,
        },
      );
    } catch (e) {
      fail(e);
    }
  };
  const columns: Column<Participant>[] = [
    {
      key: "person",
      header: t("ui:users.col.person"),
      render: (p) => <PersonCell name={p.displayName || p.email} sub={p.displayName ? p.email : undefined} />,
    },
    ...(showTeam
      ? [
          {
            key: "team",
            header: t("ui:users.col.team"),
            render: (p: Participant) => <Pill>{p.team?.displayName ?? "—"}</Pill>,
          },
        ]
      : []),
    {
      key: "domain",
      header: t("ui:users.col.domain"),
      render: (p) => (
        <div className="gp-person-text">
          <span className="t-body">{p.domain}</span>
          {p.domainClass ? (
            <StatusPill domain="domain" value={p.domainClass} label={t(`ui:domainClass.${p.domainClass}`)} />
          ) : null}
        </div>
      ),
    },
    {
      key: "sponsor",
      header: t("ui:users.col.sponsor"),
      render: (p) =>
        p.sponsor ? (
          <PersonCell name={p.sponsor.displayName} avatar={false} />
        ) : has(p, "no_sponsor") ? (
          <Flag risk="no_sponsor" />
        ) : (
          "—"
        ),
    },
    {
      key: "expiry",
      header: t("ui:users.col.expiry"),
      render: (p) =>
        p.expiryDate ? (
          <div className="gp-person-text">
            <span className="t-body">{formatDate(p.expiryDate, locale, tz)}</span>
            {p.expiryStatus === "expired" ? (
              <Flag risk="expired" />
            ) : p.expiryStatus === "expiring_soon" ? (
              <Flag risk="expiring_soon" />
            ) : null}
          </div>
        ) : has(p, "no_expiry") ? (
          <Flag risk="no_expiry" />
        ) : (
          "—"
        ),
    },
    {
      key: "reason",
      header: t("ui:users.col.reason"),
      render: (p) =>
        p.reason ? (
          p.reason.otherText ? (
            `${p.reason.label}: ${p.reason.otherText}`
          ) : (
            p.reason.label
          )
        ) : has(p, "no_reason") ? (
          <Flag risk="no_reason" />
        ) : (
          "—"
        ),
    },
    {
      key: "risk",
      header: t("ui:users.col.risk"),
      render: (p) => (p.worstRisk ? <Flag risk={p.worstRisk} /> : "—"),
    },
    {
      key: "status",
      header: t("ui:users.col.status"),
      render: (p) => (
        <StatusPill
          domain="outcome"
          // a removal in progress outranks the last decision: the row must say the guest is about to go
          value={p.openRemovalId ? "remove_requested" : (p.reviewOutcome ?? "not_reviewed")}
          label={t(
            p.openRemovalId
              ? "ui:outcome.remove_requested"
              : // an exception counts (BR-207) only once approved: until then the row says it is waiting
                p.reviewOutcome === "exception" && !p.activeExceptionId
                ? "ui:outcome.exception_pending"
                : `ui:outcome.${p.reviewOutcome ?? "not_reviewed"}`,
          )}
        />
      ),
    },
    ...(onEdit || onRemove || rows?.some((r) => r.openRemovalId)
      ? [
          {
            key: "actions",
            header: t("ui:table.actions"),
            render: (p: Participant) => (
              <div className="gp-row">
                {onEdit ? (
                  <Button
                    variant="ghost"
                    disabledReason={s.gate("PARTICIPANT_EDIT", p.teamId)}
                    onClick={() => onEdit(p)}
                  >
                    {editLabel ?? t("ui:users.edit")}
                  </Button>
                ) : null}
                {onRemove && p.presence === "detected" && !p.openRemovalId ? (
                  <Button
                    variant="ghost"
                    disabledReason={s.gate("REMOVAL_REQUEST", p.teamId)}
                    onClick={() => onRemove(p)}
                  >
                    {t("ui:users.remove")}
                  </Button>
                ) : null}
                {p.openRemovalId ? (
                  <Button
                    variant="ghost"
                    disabledReason={s.gate("REMOVAL_REQUEST", p.teamId)}
                    onClick={() => setCancelFor(p)}
                  >
                    {t("ui:removal.cancel")}
                  </Button>
                ) : null}
              </div>
            ),
          },
        ]
      : []),
  ];
  return (
    <>
      <Dialog
        open={cancelFor !== null}
        title={t("ui:removal.cancelTitle")}
        description={t("ui:removal.cancelBody", { name: cancelFor?.displayName ?? cancelFor?.email ?? "" })}
        onClose={() => setCancelFor(null)}
        footer={
          <>
            <Button onClick={() => setCancelFor(null)}>{t("common:cancel")}</Button>
            <Button
              variant="primary"
              disabled={cancel.isPending}
              onClick={() => cancelFor && void cancelRemoval(cancelFor)}
            >
              {t("ui:removal.cancel")}
            </Button>
          </>
        }
      >
        <span />
      </Dialog>
      <DataTable
        caption={t("ui:users.caption")}
        columns={columns}
        rows={rows}
        loading={loading}
        error={error}
        onRetry={onRetry}
        errorTitle={t("ui:table.error")}
        retryLabel={t("common:retry")}
        rowKey={(p) => p.id}
        emptyTitle={t("ui:users.empty")}
        sticky={sticky}
        selectable={selectable}
        selected={selected}
        onSelectionChange={onSelect}
        selectAllLabel={t("ui:users.selectAll")}
        selectRowLabel={(p) => t("ui:users.selectRow", { name: p.displayName || p.email })}
        bulkBar={bulkBar}
      />
    </>
  );
}

/** Edit sponsor, expiry, reason and notes outside a decision (BR-050…BR-059, BR-201); a version conflict asks for a reload (BR-211). */
export function ParticipantDrawer({
  participant,
  onClose,
}: {
  participant: Participant | null;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const fail = useHandleApiError();
  const reasons = useGet<Reason[]>("/api/v1/access-reasons", undefined, { enabled: participant !== null });
  const patch = useWrite<Participant, Schema<"ParticipantUpdate">>("PATCH", "/api/v1/participants/:id", [
    "/api/v1/participants",
    "/api/v1/teams",
    "/api/v1/dashboard",
  ]);
  const accept = useWrite<Participant>("POST", "/api/v1/participants/:id/accept-suggested-sponsor", [
    "/api/v1/participants",
    "/api/v1/dashboard",
  ]);
  const [sponsor, setSponsor] = useState<PersonRef | null | undefined>(undefined);
  const [expiry, setExpiry] = useState<string | undefined>();
  const [reasonId, setReasonId] = useState<string | undefined>();
  const [other, setOther] = useState<string | undefined>();
  const [notes, setNotes] = useState<string | undefined>();
  const [errors, setErrors] = useState<Record<string, string>>({});
  if (!participant) return null;
  const p = participant;
  const curSponsor = sponsor === undefined ? (p.sponsor ?? null) : sponsor;
  const options = (reasons.data ?? [])
    .filter((r) => !r.retired || r.id === p.reason?.id)
    .map((r) => ({ value: r.id ?? "", label: r.label ?? "" }));
  const other_ =
    (reasonId ?? p.reason?.id) &&
    (reasons.data ?? []).find((r) => r.id === (reasonId ?? p.reason?.id))?.key === "other";
  const save = () =>
    patch.mutate(
      {
        params: { id: p.id },
        body: {
          version: p.version,
          ...(sponsor !== undefined ? { sponsorPersonId: sponsor?.id ?? null } : {}),
          ...(expiry !== undefined ? { expiryDate: expiry || null } : {}),
          ...(reasonId !== undefined ? { accessReasonId: reasonId || null } : {}),
          ...(other !== undefined ? { reasonOtherText: other } : {}),
          ...(notes !== undefined ? { notes } : {}),
        },
      },
      {
        onSuccess: onClose,
        onError: (e) => {
          setErrors(fieldErrorsOf(e));
          fail(e);
        },
      },
    );
  return (
    <Dialog
      open
      onClose={onClose}
      title={p.displayName || p.email}
      description={p.email}
      footer={
        <>
          <Button onClick={onClose}>{t("common:cancel")}</Button>
          <Button variant="primary" onClick={save}>
            {t("common:save")}
          </Button>
        </>
      }
    >
      {p.suggestedSponsor && !p.sponsor ? (
        <Banner
          tone="info"
          action={
            <Button
              onClick={() => accept.mutate({ params: { id: p.id } }, { onSuccess: onClose, onError: fail })}
            >
              {t("ui:users.acceptSuggested")}
            </Button>
          }
        >
          {t("ui:users.suggested", { name: p.suggestedSponsor.displayName })}
        </Banner>
      ) : null}
      <Field label={t("ui:users.col.sponsor")} error={errors["sponsorPersonId"]}>
        <PersonPicker label={t("ui:users.col.sponsor")} value={curSponsor} onChange={(v) => setSponsor(v)} />
      </Field>
      <Field label={t("ui:users.col.expiry")} error={errors["expiryDate"]}>
        <DateField value={expiry ?? p.expiryDate ?? ""} onChange={(e) => setExpiry(e.target.value)} />
      </Field>
      <Field label={t("ui:users.col.reason")} error={errors["accessReasonId"]}>
        <SelectX
          options={[{ value: "", label: t("ui:users.noReason") }, ...options]}
          defaultValue={p.reason?.id ?? ""}
          onChange={(e) => setReasonId(e.target.value)}
        />
      </Field>
      {other_ ? (
        <Field label={t("ui:users.reasonOther")} error={errors["reasonOtherText"]}>
          <Input defaultValue={p.reason?.otherText ?? ""} onChange={(e) => setOther(e.target.value)} />
        </Field>
      ) : null}
      <Field label={t("ui:users.notes")}>
        <Textarea defaultValue={p.notes ?? ""} onChange={(e) => setNotes(e.target.value)} />
      </Field>
    </Dialog>
  );
}

interface Preview {
  mode: Schema<"RemovalMode">;
  outcome:
    | "record_only"
    | "needs_admin_validation"
    | "scheduled_after_delay"
    | "executes_immediately"
    | "manual_only";
  executeAfterHours?: number;
  otherControlledTeams?: Schema<"TeamRef">[];
  activeExceptionWillBeRevoked?: boolean;
  warnsOutsideScope?: boolean;
}

/** Removal preview and confirmation: the text names the exact consequence of the mode (BR-164, BR-165, BR-213, BR-215, BR-217). */
export function RemovalDialog({
  participant,
  onClose,
}: {
  participant: Participant | null;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const fail = useHandleApiError();
  const preview = useGet<Preview>(
    `/api/v1/participants/${participant?.id ?? "none"}/removal-preview`,
    undefined,
    { enabled: participant !== null },
  );
  const raise = useWrite<unknown, Schema<"RemovalRaise">>(
    "POST",
    "/api/v1/participants/:id/removal-requests",
    ["/api/v1/participants", "/api/v1/removals", "/api/v1/exceptions", "/api/v1/teams", "/api/v1/dashboard"],
  );
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  if (!participant) return null;
  const p = preview.data;
  const irreversible = p?.outcome === "executes_immediately" || p?.outcome === "scheduled_after_delay";
  const reasonMissing = reason.trim().length === 0;
  return (
    <Dialog
      open
      onClose={onClose}
      title={t("ui:removal.title", { name: participant.displayName || participant.email })}
      description={t("ui:removal.lead")}
      footer={
        <>
          <Button onClick={onClose}>{t("common:cancel")}</Button>
          <Button
            variant={irreversible ? "danger" : "primary"}
            disabledReason={
              !p
                ? t("common:loading")
                : reasonMissing
                  ? t("ui:removal.reasonRequired")
                  : irreversible && !confirmed
                    ? t("ui:removal.confirmRequired")
                    : undefined
            }
            onClick={() =>
              raise.mutate(
                {
                  params: { id: participant.id },
                  body: {
                    reason,
                    participantVersion: participant.version,
                    ...(irreversible ? { confirmIrreversible: true } : {}),
                  },
                },
                { onSuccess: onClose, onError: fail },
              )
            }
          >
            {t(`ui:removal.action.${p?.outcome ?? "record_only"}`)}
          </Button>
        </>
      }
    >
      {p ? (
        <>
          <Banner tone={irreversible ? "warn" : "info"}>
            {t(`ui:removal.outcome.${p.outcome}`, { hours: p.executeAfterHours ?? 0 })}
          </Banner>
          {p.mode === "entra_account" || p.warnsOutsideScope ? (
            <Banner tone="warn">{t("ui:removal.outsideScope")}</Banner>
          ) : null}
          {(p.otherControlledTeams ?? []).length > 0 ? (
            <Banner tone="warn">
              {t("ui:removal.otherTeams", {
                teams: (p.otherControlledTeams ?? []).map((x) => x.displayName).join(", "),
              })}
            </Banner>
          ) : null}
          {p.activeExceptionWillBeRevoked ? (
            <Banner tone="warn">{t("ui:removal.exceptionRevoked")}</Banner>
          ) : null}
        </>
      ) : null}
      <Field label={t("ui:removal.reason")}>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
      {irreversible ? (
        <Checkbox checked={confirmed} onChange={setConfirmed} label={t("ui:removal.irreversible")} />
      ) : null}
    </Dialog>
  );
}
